import { useRouter, usePathname, useSearchParams } from "next/navigation";
import { useEffect } from "react";
import type { AppRouterInstance } from "next/dist/shared/lib/app-router-context.shared-runtime";
import { useAuth } from "@/context/providers/AuthProvider";
import { redirectManager } from "@/lib/auth";

/**
 * Hook to handle authentication redirects while preserving the current page
 * @param requireAuth - Whether authentication is required for the current page
 * @param fallbackRedirect - Fallback redirect URL if no current page (defaults to /account)
 */
export function useRedirectToLogin(
  requireAuth: boolean = true,
  fallbackRedirect: string = "/account"
) {
  const { user, loading: authLoading } = useAuth();
  const router = useRouter();
  const pathname = usePathname();
  const searchParams = useSearchParams();

  useEffect(() => {
    // Only redirect if auth is required, auth is not loading, and user is not authenticated
    if (requireAuth && !authLoading && !user) {
      // Build the current URL with search params, preserving all parameters
      const currentUrl = searchParams.toString()
        ? `${pathname}?${searchParams.toString()}`
        : pathname;

      // Don't redirect if already on login page to avoid infinite loops
      // Also check pathname separately to catch cases where query params might differ
      if (pathname !== "/login" && currentUrl !== "/login") {
        // Store redirect URL in sessionStorage
        redirectManager.storeRedirect(currentUrl);
        router.push("/login");
      }
    }
  }, [requireAuth, authLoading, user, router, pathname, searchParams]);

  return { user, loading: authLoading };
}

/**
 * Utility function to redirect to login from anywhere in the app
 * @param router - Next.js router instance
 * @param fallbackRedirect - Fallback redirect URL if no current page
 */
export function redirectToLogin(
  router: Pick<AppRouterInstance, "push">,
  fallbackRedirect: string = "/account"
) {
  if (typeof window !== "undefined") {
    // Capture the complete URL including pathname, search params, and hash
    const currentUrl =
      window.location.pathname + window.location.search + window.location.hash;

    // Don't redirect if already on login page to avoid infinite loops
    const redirectUrl = currentUrl !== "/login" ? currentUrl : fallbackRedirect;

    // Store redirect URL in sessionStorage
    redirectManager.storeRedirect(redirectUrl);
    router.push("/login");
  } else {
    router.push("/login");
  }
}

/**
 * Utility function to redirect to login with a specific destination
 * @param router - Next.js router instance
 * @param destination - The URL to redirect to after login
 */
export function redirectToLoginWithDestination(
  router: Pick<AppRouterInstance, "push">,
  destination: string,
  mode: "sign-in" | "sign-up" = "sign-in"
) {
  // Store redirect URL in sessionStorage
  redirectManager.storeRedirect(destination);
  router.push(mode === "sign-up" ? "/login?mode=sign-up" : "/login");
}
